import { useCallback, useState } from 'react';
import { trpc } from '../lib/trpc';
import { useAuth } from './useAuth';
import { Button } from '../components/ui/Button';

type ResendState = 'idle' | 'success' | 'error';

interface GateModalProps {
  open: boolean;
  email: string | null;
  onClose: () => void;
}

const GateModal = ({ open, email, onClose }: GateModalProps) => {
  const [resendState, setResendState] = useState<ResendState>('idle');
  const resendMutation = trpc.auth.resendVerification.useMutation({
    onSuccess: () => setResendState('success'),
    onError: () => setResendState('error'),
  });

  if (!open) return null;

  const handleResend = () => {
    if (!email) return;
    setResendState('idle');
    resendMutation.mutate({ email });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-verification-gate-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-gray-50 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                className="h-6 w-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h2
                id="email-verification-gate-title"
                className="text-lg font-semibold text-gray-900"
              >
                Verify your email to continue
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                This action requires a verified email address. Check your inbox for a
                verification link{email ? ` sent to ${email}` : ''} — or request a new one below.
              </p>
              {resendState === 'success' && (
                <p className="mt-3 text-sm text-green-700">
                  ✓ Verification email sent. Please check your inbox.
                </p>
              )}
              {resendState === 'error' && (
                <p className="mt-3 text-sm text-red-700">
                  Failed to send verification email. Please try again later.
                </p>
              )}
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleResend}
              disabled={!email || resendMutation.isPending}
              loading={resendMutation.isPending}
            >
              {resendMutation.isPending ? 'Sending...' : 'Resend verification email'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export interface EmailVerificationGate {
  requireVerified: () => boolean;
  gateModal: JSX.Element;
}

export const useEmailVerificationGate = (): EmailVerificationGate => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const requireVerified = useCallback((): boolean => {
    if (!user) return true;
    if (user.emailVerified) return true;
    setOpen(true);
    return false;
  }, [user]);

  const gateModal = (
    <GateModal open={open} email={user?.email ?? null} onClose={() => setOpen(false)} />
  );

  return { requireVerified, gateModal };
};
